/**
 * Pure computation for the tenant eligibility / recommendation engine.
 *
 * All inputs come in as parameters; nothing is read from component state or
 * closure.  No React state setters are called inside this function.
 *
 * @param {object} params
 * @param {object}   params.tenantResults          - Map of tenant result objects keyed by tenant id.
 * @param {Array}    params.selectedTenant          - Array of selected tenant entries.
 * @param {object}   params.customPolicyDetails     - Policy details including threshold and pillarWeights.
 * @param {boolean}  params.giveInsurance           - Whether insurance uplift is applied.
 * @param {number|string} params.finalEffortRate    - Effort-rate value used for guarantor logic.
 * @param {boolean}  params.enabled                 - External "enabled" flag (overrides verdict for insurance suggestion).
 * @param {boolean}  params.deaWorkspace            - Whether current workspace is a DEA workspace.
 * @param {object}   params.user                    - Current user object.
 * @param {string}   params.lastVerdict             - Previous verdict stored in component state.
 * @param {object}   params.lastRecommendationFlags - Previous recommendation flags stored in component state.
 * @param {object}   params.STATUS_STYLES           - Constant map of status style objects { red, green, yellow }.
 * @param {Function} params.translate               - i18n translation function (_t).
 * @param {boolean}  [params.isReport=false]        - When true returns a structured report object.
 * @param {object}   [params.guarantorResults=null] - Map of guarantor result objects keyed by guarantor id (optional).
 *
 * @returns {{
 *   result: object|null,
 *   sideEffects: {
 *     verdict: string|null,
 *     recommendationFlags: object|null,
 *     showAddGuarantor: boolean|null,
 *     nextStatusStyle: object|null,
 *   }
 * }}
 *
 * `result`      – the report payload (when isReport=true) or null.
 * `sideEffects` – values the component should persist to state after calling this function.
 *                 Any field set to null means "no change needed".
 */
import { DEBUG_CALCULATION } from "../helper/common";

export function computeFinalResult({
  tenantResults,
  selectedTenant,
  customPolicyDetails,
  giveInsurance,
  finalEffortRate,
  enabled,
  deaWorkspace,
  user,
  lastVerdict,
  lastRecommendationFlags,
  STATUS_STYLES,
  translate: _t,
  isReport = false,
  guarantorResults = null,
  aggregationStrategy,
}) {
  // Neutral return helper – keeps callers simple.
  const noChange = () => ({
    result: null,
    sideEffects: {
      verdict: null,
      recommendationFlags: null,
      showAddGuarantor: null,
      nextStatusStyle: null,
    },
  });

  try {
    const DECISION_THRESHOLD = customPolicyDetails.threshold;
    const STRATEGY =
      aggregationStrategy || customPolicyDetails?.aggregationStrategy || "MIN";
    const values = Object.values(tenantResults || {});
    const guarantorValues = Object.values(guarantorResults || {});
    const allApplicantValues = [...values, ...guarantorValues];
    let recommendationReasons = [];

    // ─── helpers ────────────────────────────────────────────────────────────

    const aggregateValue = (vals) => {
      if (STRATEGY === "MAX") return Math.max(...vals);
      if (STRATEGY === "MEAN")
        return vals.reduce((a, b) => a + b, 0) / vals.length;
      return Math.min(...vals);
    };

    const getErrorReasonText = (label, fallbackKey) => {
      const descKey = `${label}DescInOverview`;
      const descText = label ? _t(descKey) : "";

      if (descText && descText !== descKey) return descText;

      const translatedLabel = label ? _t(label) : "";

      if (translatedLabel && translatedLabel !== label) return translatedLabel;

      return fallbackKey || label || "";
    };

    /**
     * Decides whether a guarantor recommendation is appropriate.
     * Receives the computed weightObj so it has no side-channel dependency.
     */
    const shouldAllowGuarantorRecommendation = (
      finalScoreValue = 0,
      weightObj
    ) => {
      const finalIncomeAffordability = weightObj?.INCOME_AFFORDABILITY ?? 100;
      const effortRateValue = Number(finalEffortRate ?? 0);

      if (finalIncomeAffordability < 60) return true;
      if (effortRateValue > 35) return true;
      if (finalScoreValue >= 65 && finalScoreValue < 67) return true;

      return false;
    };

    // ─── 0️⃣  Guard: no tenants selected ───────────────────────────────────
    if (!selectedTenant || selectedTenant.length === 0) return noChange();

    // ─── 1️⃣  Hard-fail check ───────────────────────────────────────────────
    // A hard fail from either a tenant or a selected guarantor rejects the
    // application. Guarantors count as full applicants: they take part in
    // score/pillar aggregation and their eligibility checks affect the decision.
    const hasHardFail = allApplicantValues.some((item) => item?.isHardFail);

    if (DEBUG_CALCULATION) {
      console.log(
        "[computeFinalResult] applicant values count:",
        allApplicantValues.length
      );
      console.log("[computeFinalResult] hasHardFail:", hasHardFail);
      allApplicantValues.forEach((item, idx) => {
        console.log(`[computeFinalResult] value[${idx}]:`, {
          label: item?.label,
          isHardFail: item?.isHardFail,
          errorEntries: item?.errorEntries,
          finalScore: item?.finalScore ?? item?.data?.finalScore,
        });
      });
    }

    if (hasHardFail) {
      const hardFailReasons = allApplicantValues.flatMap((item) =>
        (item?.errorEntries || []).map(([key, value], index) => ({
          id: `${item?.label || "tenant"}-${key}-${index}`,
          name: item?.label || "Tenant",
          reason: getErrorReasonText(value?.label, key),
        }))
      );

      // Side-effects to persist
      const rejectedFlags = {
        addGuarantor: false,
        addMoreGuarantor: false,
        buyInsurance: false,
        completeProfile: false,
      };

      const flagsChanged = Object.keys(rejectedFlags).some(
        (k) => (lastRecommendationFlags || {})[k] !== rejectedFlags[k]
      );

      const nextStatusStyle =
        user?.current_workspace?.dea_rules_activated || deaWorkspace
          ? null // don't change status when DEA workspace
          : STATUS_STYLES.red;

      if (isReport) {
        const tenantApplicants = buildApplicants(tenantResults);
        const guarantorApplicants = guarantorResults
          ? buildApplicants(guarantorResults)
          : [];
        const allApplicants = [...tenantApplicants, ...guarantorApplicants];

        return {
          result: {
            report: {
              case: {
                isHardFail: true,
                finalScore: 0,
                applicantCount: allApplicants.length,
                decision: "Rejected",
                recommendation: hardFailReasons.map((r) => r.reason),
              },
              applicants: allApplicants, // Include both tenants and guarantors
            },
          },
          sideEffects: {
            verdict: lastVerdict !== "Rejected" ? "Rejected" : null,
            recommendationFlags: flagsChanged ? rejectedFlags : null,
            showAddGuarantor: false,
            nextStatusStyle,
          },
        };
      }
      return {
        result: null,
        sideEffects: {
          verdict: lastVerdict !== "Rejected" ? "Rejected" : null,
          recommendationFlags: flagsChanged ? rejectedFlags : null,
          showAddGuarantor: false,
          nextStatusStyle,
        },
      };
    }

    // ─── 2️⃣  Validate applicant scores and pillars ─────────────────────────
    // Do not publish a partial decision while tenant/guarantor calculations are
    // incomplete, and never allow an invalid pillar to produce a NaN score.
    const scores = allApplicantValues
      .map((item) => item?.data?.finalScore ?? item?.finalScore)
      .filter((score) => Number.isFinite(score));

    if (
      !values.length ||
      !allApplicantValues.length ||
      scores.length !== allApplicantValues.length
    )
      return noChange();

    const pillarWeights = customPolicyDetails?.policyConfig?.pillarWeights;
    if (!Array.isArray(pillarWeights) || !pillarWeights.length)
      return noChange();

    const requiredPillarKeys = pillarWeights
      .filter((item) => Number(item?.weight) > 0)
      .map((item) => item.pillarKey);
    const hasInvalidPillars = allApplicantValues.some(
      (item) =>
        !item?.pillars ||
        requiredPillarKeys.some((key) => !Number.isFinite(item.pillars[key]))
    );
    if (hasInvalidPillars) return noChange();

    // ─── 3️⃣  Get pillar values across all applicants ───────────────────
    // Tenants and guarantors aggregate together: a guarantor is a full
    // applicant/candidate, so MIN / MAX / MEAN span every applicant.
    const pillarValuesMap = [...values, ...guarantorValues].reduce(
      (acc, item) => {
        if (!item || !item.pillars) return acc;

        Object.entries(item.pillars).forEach(([key, value]) => {
          if (acc[key] !== undefined) {
            acc[key] = Array.isArray(acc[key])
              ? [...acc[key], value]
              : [acc[key], value];
          } else {
            acc[key] = [value];
          }
        });

        return acc;
      },
      {}
    );

    const weightObj = {};
    Object.keys(pillarValuesMap).forEach((key) => {
      weightObj[key] = aggregateValue(pillarValuesMap[key]);
    });

    // ─── 4️⃣  Calculate weighted result using policyConfig.pillarWeights ────
    const weightMap = Object.fromEntries(
      pillarWeights.map((item) => [item.pillarKey, item.weight])
    );

    const result = Object.entries(weightObj).reduce((sum, [key, value]) => {
      const weight = weightMap[key] || 0;
      return sum + value * weight;
    }, 0);

    const upliftedScore = result + 8;
    const effectiveScore = giveInsurance ? upliftedScore : result;

    let effectiveVerdict;
    if (effectiveScore < DECISION_THRESHOLD.MIN) {
      effectiveVerdict = "Rejected";
    } else if (effectiveScore > 70) {
      effectiveVerdict = "Approved";
    } else {
      effectiveVerdict = "Conditional";
    }

    // eslint-disable-next-line no-unused-vars
    const canApproveWithInsurance =
      upliftedScore > DECISION_THRESHOLD.MAX &&
      result <= DECISION_THRESHOLD.MAX;

    // ─── Decision logic ──────────────────────────────────────────────────────
    let verdict;
    if (hasHardFail) {
      verdict = "Rejected";
    } else if (result < DECISION_THRESHOLD.MIN) {
      verdict = "Rejected";
    } else if (result > DECISION_THRESHOLD.MAX) {
      verdict = "Approved";
    } else {
      verdict = "Conditional";
    }

    if (DEBUG_CALCULATION) {
      console.log(
        "[computeFinalResult] result:",
        result,
        "upliftedScore:",
        upliftedScore,
        "effectiveScore:",
        effectiveScore
      );
      console.log(
        "[computeFinalResult] verdict:",
        verdict,
        "effectiveVerdict:",
        effectiveVerdict
      );
      console.log("[computeFinalResult] weightObj:", weightObj);
    }

    // ─── Status style ────────────────────────────────────────────────────────
    let nextStatusStyle = null;
    if (!(user?.current_workspace?.dea_rules_activated || deaWorkspace)) {
      let next;
      if (hasHardFail || verdict === "Rejected") {
        next = STATUS_STYLES.red;
      } else if (verdict === "Approved" || enabled) {
        next = STATUS_STYLES.green;
      } else {
        next = STATUS_STYLES.yellow;
      }
      if (DEBUG_CALCULATION) {
        console.log(
          "[computeFinalResult] nextStatusStyle:",
          next?.text,
          "enabled:",
          enabled
        );
      }
      nextStatusStyle = next; // caller will skip update if prev === next
    }

    // ─── 5️⃣  Profile completion check ──────────────────────────────────────
    const incompleteTenants = [];
    // (Commented out in original; left as empty array to preserve logic flow)

    // ─── 6️⃣  Guarantor + Insurance recommendation flags ─────────────────────
    let recommendationFlags = {
      addGuarantor: false,
      addMoreGuarantor: false,
      buyInsurance: false,
      completeProfile: false,
    };

    if (incompleteTenants.length > 0) {
      recommendationFlags.completeProfile = true;
    }

    if (verdict === "Conditional" && incompleteTenants.length === 0) {
      const hasGuarantor = selectedTenant.some(
        (entry) => (entry?.tenantid?.gurantor_id || []).length > 0
      );
      const willApproveWithInsurance = upliftedScore > DECISION_THRESHOLD.MAX;
      const allowGuarantor = shouldAllowGuarantorRecommendation(
        result,
        weightObj
      );

      if (allowGuarantor) {
        if (!hasGuarantor) {
          recommendationFlags.addGuarantor = true;
        } else if (!willApproveWithInsurance) {
          recommendationFlags.addMoreGuarantor = true;
        }
      }

      if (willApproveWithInsurance) {
        recommendationFlags.buyInsurance = true;
      }
    }

    // ─── 6B. Insurance suggestion even for Approved ──────────────────────────
    // Not showing insurance in case of approved
    if (enabled) {
      recommendationFlags.buyInsurance = true;
    }

    // ─── Build recommendation reasons ────────────────────────────────────────
    // Threshold rejection (not hard-fail) must appear as blocking reason
    if (verdict === "Rejected" && !hasHardFail) {
      const thresholdText = getErrorReasonText(
        "scoreBelowThreshold",
        "scoreBelowThreshold"
      );
      recommendationReasons.push(
        thresholdText !== "scoreBelowThreshold"
          ? thresholdText
          : `Score below approval threshold (${result.toFixed(1)} < ${DECISION_THRESHOLD.MIN})`
      );
    }

    if (recommendationFlags.completeProfile) {
      recommendationReasons.push(
        "Incomplete profile - Missing required documents"
      );
    }

    if (recommendationFlags.addGuarantor) {
      if (weightObj?.INCOME_AFFORDABILITY < 60) {
        recommendationReasons.push(
          "Low income affordability - Add a guarantor"
        );
      }
      if (Number(finalEffortRate) > 35) {
        recommendationReasons.push("High effort rate - Add a guarantor");
      }
    }

    if (recommendationFlags.addMoreGuarantor) {
      recommendationReasons.push(
        "Existing guarantor is insufficient - Add more guarantor"
      );
    }

    if (recommendationFlags.buyInsurance) {
      if (verdict === "Approved" || enabled) {
        recommendationReasons.push(
          "Optional: Buying insurance will strengthen application"
        );
      } else {
        recommendationReasons.push(
          "Buying Skor Insurance will make this profile Approved"
        );
      }
    }

    const showAddGuarantor =
      recommendationFlags.addGuarantor || recommendationFlags.addMoreGuarantor;

    // ─── Determine whether recommendation flags changed ───────────────────────
    const prev = lastRecommendationFlags || {};
    const flagsChanged =
      Object.keys(recommendationFlags).some(
        (k) => prev[k] !== recommendationFlags[k]
      ) || Object.keys(prev).length !== Object.keys(recommendationFlags).length;

    // ─── 5️⃣  Final output ────────────────────────────────────────────────────
    if (isReport) {
      const tenantApplicants = buildApplicants(tenantResults);
      const guarantorApplicants = guarantorResults
        ? buildApplicants(guarantorResults)
        : [];
      const allApplicants = [...tenantApplicants, ...guarantorApplicants];
      console.log("Final applicants result:", result);
      return {
        result: {
          report: {
            case: {
              isHardFail:
                Object.values(tenantResults || {}).some((t) => t?.isHardFail) ||
                (guarantorResults &&
                  Object.values(guarantorResults).some((g) => g?.isHardFail)) ||
                false,
              finalScore: result,
              applicantCount: allApplicants.length,
              decision: verdict,
              recommendation: recommendationReasons || [],
            },
            applicants: allApplicants, // Include both tenants and guarantors
          },
        },
        sideEffects: {
          verdict: lastVerdict !== verdict ? verdict : null,
          recommendationFlags: flagsChanged ? recommendationFlags : null,
          showAddGuarantor,
          nextStatusStyle,
        },
      };
    }

    return {
      result: null,
      sideEffects: {
        verdict: lastVerdict !== verdict ? verdict : null,
        recommendationFlags: flagsChanged ? recommendationFlags : null,
        showAddGuarantor,
        nextStatusStyle,
      },
    };
  } catch (err) {
    console.log(err);
    return {
      result: null,
      sideEffects: {
        verdict: null,
        recommendationFlags: null,
        showAddGuarantor: null,
        nextStatusStyle: null,
      },
    };
  }
}

// ─── Private helpers ──────────────────────────────────────────────────────────

/**
 * Builds the `applicants` array that is shared by both hard-fail and normal
 * report paths.  Kept here to avoid duplication.
 */
function buildApplicants(results) {
  const applicants = Object.values(results || {}).map((t) => {
    // Determine if this is a guarantor or tenant based on the data structure
    // Priority: explicit role field -> isGuarantor flag -> fallback detection
    const isGuarantor =
      t?.role === "guarantor" ||
      t?.isGuarantor ||
      (t?.vals &&
        Object.keys(t?.vals || {}).some(
          (key) => key.includes("guarantor") || key.includes("GUARANTOR")
        ));

    // Determine insurability: not insurable if hard fail, otherwise insurable
    const isInsurable = !t?.isHardFail;

    const applicant = {
      name: t.label || (isGuarantor ? "Guarantor" : "Tenant"),
      _id: t?._id,
      tenantData: t?.tenantData,
      role: t?.role || (isGuarantor ? "guarantor" : "tenant"),
      isInsurable: isInsurable,
      isHardFail: !!t?.isHardFail,
      vals: t?.vals || {},
      errorEntries: t?.errorEntries || [],
      finalScore: Number((t.finalScore ?? 0).toFixed(2)),
      pillars: {
        incomeAffordability: t?.pillars?.INCOME_AFFORDABILITY ?? 0,
        employmentStability: t?.pillars?.EMPLOYMENT_STABILITY ?? 0,
        financialBehaviour: t?.pillars?.FINANCIAL_BEHAVIOUR ?? 0,
        creditDefaultRisk: t?.pillars?.CREDIT_DEFAULT_RISK ?? 0,
        documentationStrength: t?.pillars?.DOCUMENTATION_STRENGTH ?? 0,
      },
      subScores: {
        sectorCyclicality: t?.subScores?.SECTOR_CYCLICALITY,
        tenure: t?.subScores?.TENURE,
        employmentPredictability: t?.subScores?.EMPLOYMENT_PREDICTABILITY,
        employerStability: t?.subScores?.EMPLOYER_STABILITY,
        incomeConsistency: t?.subScores?.INCOME_CONSISTENCY,
        incomeVerification: t?.subScores?.INCOME_VERIFICATION,
        effortRate: t?.subScores?.EFFORT_RATE_SCORE,
        financialSkor: t?.subScores?.FINANCIAL_SKOR,
        bankBehaviour: t?.subScores?.BANK_BEHAVIOUR,
        rentPaymentBehaviour: t?.subScores?.RENT_PAYMENT_BEHAVIOUR,
        defaultHistory: t?.subScores?.DEFAULT_HISTORY,
        riskMitigation: t?.subScores?.RISK_MITIGATION_SUB_SCORE,
        documentation: {
          ...t?.subScores?.DOCUMENTATION,
          missingDocs: t?.vals?.MISSING_DOCS || [],
        },
        forensics: t?.subScores?.FORENSICS,
      },
    };

    return applicant;
  });

  console.log("buildApplicants - Total applicants:", applicants.length);
  console.log(
    "buildApplicants - Breakdown:",
    applicants.reduce((acc, a) => {
      acc[a.role] = (acc[a.role] || 0) + 1;
      return acc;
    }, {})
  );

  return applicants;
}
